import { CSSResult, html, unsafeCSS } from 'lit';
import { unsafeHTML } from 'lit-html/directives/unsafe-html.js';

import { property, query } from 'lit/decorators.js';
import DERModuleElement from '~/modules/utils/der-module-element';

import { styles } from './date-picker.styles';

import '~/ui/components/calendar';
import '~/ui/components/tablist';
import '../sales-sites';

import { AppointmentBookingDatepickerTranslations } from '../appointment-booking.interface';
import { Calendar } from '~/ui/components/calendar';

export class DatePicker extends DERModuleElement {
    static override styles: CSSResult = unsafeCSS(styles);

    @property({ type: Number, attribute: 'booking-day-offset' })
    bookingDayOffset: number = 3;

    @property({ type: Array })
    openingWeekdays: number[] = [];

    @property({ type: Array })
    specialSelectableDates: string[] = [];

    @property({ type: String, attribute: 'opportunity-owning-team' })
    opportunityOwningTeam?: string = '';

    @property({ type: String, attribute: 'sales-site-url' })
    salesSiteUrl: string = '';

    @property({ type: Object })
    translations: AppointmentBookingDatepickerTranslations = {} as AppointmentBookingDatepickerTranslations;

    @query('der-ui-calendar') calendar!: Calendar;
    @query('form') formEl!: HTMLFormElement;
    @query('input[name="OpportunityConsultationDate"]') dateInput!: HTMLInputElement;

    /**
     * Resets the date picker component to its initial state.
     * Clears the date input, resets the calendar, and resets the form.
     */
    reset(): void {
        this.dateInput.value = '';
        this.calendar.reset();
        this.formEl.reset();
    }

    /**
     * Validates the date picker form.
     * @returns True if the form is valid, false otherwise
     */
    validate(): boolean {
        return this.formEl.reportValidity();
    }

    /**
     * Gets the form values as a record of form data entries.
     * @returns Record containing form field names and their values
     */
    getFormValue(): Record<string, FormDataEntryValue> {
        return Object.fromEntries(new FormData(this.formEl));
    }

    /**
     * Handles calendar date selection change events.
     * Updates the date input value and dispatches a calendar-date-selected event.
     * @param e CustomEvent containing the selected date
     */
    private _handleCalendarChange(e: CustomEvent) {
        e.stopPropagation();
        this.dateInput.value = e.detail?.date;
        /**
         * Event dispatch
         */
        this.dispatchEvent(
            new CustomEvent('calendar-date-selected', {
                detail: { value: this.dateInput.value },
                bubbles: true,
                composed: true,
            })
        );
    }

    /**
     * Handles sales site selection change events.
     * Resets the calendar when a new sales site is selected.
     */
    private _handleSalesSiteChanged(): void {
        this.calendar.reset();
    }

    /**
     * Handles tablist change events for consultation channel selection.
     * Prevents event propagation.
     * @param e CustomEvent from the tablist component
     */
    private _handleTablistChange(e: CustomEvent): void {
        e.stopPropagation();
    }

    /**
     * Gets the tab list options for consultation channel selection.
     * Maps consultation channel types to their display values.
     * Note: Values are mapped to language codes in the azure function before sending to CRM.
     * @returns Array of tab options with value, label, and title
     */
    get tabListOptions(): Array<{ value: string; label: string; title: string }> {
        return [
            {
                value: '948150000',
                label: `${this.translations.tabList.typeOffice}`,
                title: `${this.translations.tabList.typeOffice}`,
            },
            {
                value: '948150002',
                label: `${this.translations.tabList.typeTeams}`,
                title: `${this.translations.tabList.typeTeams}`,
            },
            {
                value: '948150001',
                label: `${this.translations.tabList.typePhone}`,
                title: `${this.translations.tabList.typePhone}`,
            },
        ];
    }

    override render() {
        return html`
            <form>
                ${this.opportunityOwningTeam
                    ? ''
                    : html`
                          <der-tailormade-appointment-booking-sales-sites
                              lang="${this.lang}"
                              name="OpportunityOwningTeam"
                              sales-site-url="${this.salesSiteUrl}"
                              placeholder=" ${this.translations?.saleSite.placeholder}"
                              ?required="${true}"
                              @sales-site-changed=${this._handleSalesSiteChanged}
                          ></der-tailormade-appointment-booking-sales-sites>
                      `}

                <div class="m-appointment-booking__subitem-wrapper">
                    <div class="headline">${unsafeHTML(this.translations?.headlines.consultationChannel)}</div>
                    <der-ui-tablist
                        value="948150000"
                        name="OpportunityConsultationChannel"
                        variant="off-default"
                        .required=${true}
                        .options=${this.tabListOptions}
                        @der.change=${this._handleTablistChange}
                    ></der-ui-tablist>
                </div>
                <div class="m-appointment-booking__subitem-wrapper">
                    <div class="headline">${this.translations?.headlines.datepicker}</div>
                    <der-ui-calendar
                        lang=${this.lang}
                        selected-date=${this.dateInput?.value}
                        booking-day-offset=${this.bookingDayOffset}
                        ?month-selection-dropdown=${true}
                        .openingWeekdays=${this.openingWeekdays}
                        .specialSelectableDates=${this.specialSelectableDates}
                        @der.change=${this._handleCalendarChange}
                    ></der-ui-calendar>
                    <div class="invisible-input-wrapper">
                        <input type="text" name="OpportunityConsultationDate" required />
                    </div>
                </div>
            </form>
        `;
    }
}
